import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import humps from 'humps';
import http from '../../shared/http';
import './EnhancedAdExamples.scss';

import Mixpanel from '../../shared/Mixpanel';

const ExampleList = props => {
  function thumbnailClass(exampleId) {
    let defaultClasses = 'enhanced-ad-example_thumbnail clickable ';

    if (exampleId == props.activeExampleId) {
      defaultClasses += 'enhanced-ad-example_thumbnail--active';
    }

    return defaultClasses;
  }

  return props.examples.map(example => {
    return (
      <div
        className="enhanced-ad-example_thumbnail-container"
        key={example.id}
        onClick={() => props.handleExampleClick(example.id)}
        onKeyDown={() => props.handleExampleClick(example.id)}
        tabIndex="0"
        role="button"
      >
        {example.creative_preview.adm ? (
          <div
            className={`${thumbnailClass(example.id)} ${props.thumbnailClass}`}
          >
            <div className="enhanced-ad-example_iframe-wrapper">
              <div
                role="button"
                tabIndex="0"
                className="mouse-blocker"
                onClick={() => props.handleExampleClick(example.id)}
                onKeyDown={() => props.handleExampleClick(example.id)}
              />
              <div
                className="enhanced-ad-example_iframe"
                dangerouslySetInnerHTML={{
                  __html: example.creative_preview.adm
                }}
              />
            </div>
          </div>
        ) : (
          <img
            className={thumbnailClass(example.id) + ` ${props.thumbnailClass}`}
            alt="Enhanced Ad Example"
            data-testid={`example-${example.id}`}
            data-example-id={example.id}
            src={example.creative_preview.thumbnail_url}
          />
        )}
        <div className="enhanced-ad-example_advertiser">
          {example.creative_preview.advertiser}
        </div>
      </div>
    );
  });
};

ExampleList.propTypes = {
  examples: PropTypes.array.isRequired,
  activeExampleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  thumbnailClass: PropTypes.string,
  handleExampleClick: PropTypes.func.isRequired
};

ExampleList.defaultProps = {
  activeExampleId: null,
  thumbnailClass: ''
};

const EnhancedAdExamples = props => {
  const [examples, setExamples] = useState([]);
  const [activeExampleId, setActiveExampleId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    http.get('/examples?limit=27').then(resp => {
      const examples = resp.data;
      setExamples(examples);
      const firstExample = examples[0];
      if (firstExample) {
        setCreative(firstExample.creative_preview);
        setActiveExampleId(firstExample.id);
      }
    });

    props.CurrentUserService.isAdmin().then(isAdmin => setIsAdmin(isAdmin));
    Mixpanel.track('View Enhanced Ad examples');
  }, []);

  const setCreative = creative => {
    let thumbnailUrl = creative.thumbnail_url;
    if (thumbnailUrl) {
      const gifRegex = /.*\.gif($|\?d=)/;

      if (thumbnailUrl.match(gifRegex)) {
        thumbnailUrl = thumbnailUrl.replace(/\?d=.*/, '');
        thumbnailUrl += `?d=${Date.now()}`;
        creative.thumbnail_url = thumbnailUrl;
      }
    }
    // Our angular service expects the creative to be camelized
    props.CreativeService.setCreative(humps.camelizeKeys(creative));
  };

  const handleExampleClick = exampleId => {
    const example = examples.find(example => example.id === exampleId);
    if (window.outerWidth < 768) {
      const creativePreviewId = example.creative_preview.id;
      window.location.href = `/enhanced/example/${creativePreviewId}`;
      return;
    }
    setActiveExampleId(exampleId);
    setCreative(example.creative_preview);
    Mixpanel.track('Click Enhanced Ad example');
  };

  const handleSearchInputChange = event => {
    setSearchInput(event.target.value.trim());
  };

  const handleAddSearch = event => {
    if (
      !savedSearches.includes(searchInput) &&
      searchInput.trim().length > 0 &&
      event.key === 'Enter'
    ) {
      setSavedSearches([...savedSearches, searchInput.trim()]);
    }
  };

  const handleDeleteSearch = search => {
    if (savedSearches.includes(search)) {
      setSavedSearches(
        savedSearches.filter(currentSearch => currentSearch !== search)
      );
    }
  };

  const searchExamples = () => {
    const allSearches =
      searchInput.length > 0
        ? savedSearches.concat(searchInput)
        : savedSearches;

    const matchSearch = creativePreview => search =>
      creativePreview.advertiser.toLowerCase().match(search.toLowerCase());

    return examples.filter(({ creative_preview }) => {
      try {
        return allSearches.some(matchSearch(creative_preview));
      } catch (parsingError) {
        return savedSearches.some(matchSearch(creative_preview));
      }
    });
  };

  const outstreamExamples = () => {
    return examples.filter(
      ({ creative_preview }) => creative_preview.action === 'hosted-video'
    );
  };

  const bannerExamples = () => {
    return examples.filter(
      ({ creative_preview }) =>
        creative_preview.action === 'clickout' ||
        creative_preview.action === 'banner'
    );
  };

  return (
    <Fragment>
      <h1 className="enhanced-ad-example_title">
        Banners and Video Ads, Automatically Enhanced to Fit In
      </h1>

      <div className="enhanced-ad-example_subtitle">
        Simply target Sharethrough as a standard display or standard outstream
        source using the banners & videos you already have, and drive 2x
        performance.
      </div>

      <div className="enhanced-ads_search-field">
        <i className="ss-search enhanced-ads_search-field_icon" />
        <input
          type="text"
          className="enhanced-ads_search-field_input"
          onChange={handleSearchInputChange}
          onKeyPress={handleAddSearch}
          placeholder="Search by brand"
        />
        <i className="enhanced-ads_search-field_help-text">
          Press Enter to save search for multiple brands
        </i>
      </div>

      <div className="enhanced-ads_search-tags-container">
        {savedSearches.length > 0 &&
          savedSearches.map(search => (
            <div className="enhanced-ads_search-tag" key={search}>
              {search}
              <button
                className="enhanced-ads_search-tag-button"
                data-testid={`delete-search-${search}`}
                onClick={() => handleDeleteSearch(search)}
              >
                {'\u00D7'}
              </button>
            </div>
          ))}
      </div>

      {savedSearches.length > 0 || searchInput.length > 0 ? (
        <div
          className="enhanced-ad-example_grid"
          data-testid="search-results-examples-list"
        >
          <ExampleList
            examples={searchExamples()}
            activeExampleId={activeExampleId}
            handleExampleClick={handleExampleClick}
          />
        </div>
      ) : (
        <Fragment>
          <div className="enhanced-ad-example_grid_title">Videos</div>
          <div className="enhanced-ad-example_grid_subtitle">
            Select any standard outstream video below to see the Sharethrough
            Enhanced version on the right.
          </div>

          <div className="enhanced-ad-example_grid">
            <ExampleList
              examples={outstreamExamples()}
              thumbnailClass="enhanced-ad-example_thumbnail--video"
              activeExampleId={activeExampleId}
              handleExampleClick={handleExampleClick}
            />
          </div>

          <div className="enhanced-ad-example_grid_title">Banners</div>
          <div className="enhanced-ad-example_grid_subtitle">
            Select any standard 300x250 banner below to see the Sharethrough
            Enhanced version on the right.
          </div>

          <div className="enhanced-ad-example_grid">
            <ExampleList
              examples={bannerExamples()}
              activeExampleId={activeExampleId}
              handleExampleClick={handleExampleClick}
            />
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

EnhancedAdExamples.propTypes = {
  CreativeService: PropTypes.shape({
    setCreative: PropTypes.func
  }).isRequired,
  CurrentUserService: PropTypes.shape({
    isAdmin: PropTypes.func.isRequired
  }).isRequired
};

export default EnhancedAdExamples;
